import { useCallback, useState } from 'react';

// Services.
import PrivateWealthTeamsService from '../../services/PrivateWealthTeamsService';

const useAumAggregations = ({
  team,
  session,
  openConfirmDialog,
  onAggregationsChange,
  onUserChange
}) => {
  const [aumAggregations, setAumAggregations] = useState([]);
  const [fetchingAumAggregations, setFetchingAumAggregations] = useState(false);
  const [fetchStatusAumAggregations, setFetchStatusAumAggregations] = useState('idle');

  const sortAggregationsByYearDesc = (aggregations) =>
    [...aggregations].sort((a, b) => {
      const yearDiff = Number(b.year ?? 0) - Number(a.year ?? 0);
      if (yearDiff !== 0) {
        return yearDiff;
      }
      return Number(b.month ?? 0) - Number(a.month ?? 0);
    });

  const fetchAumAggregations = useCallback(async () => {
    setFetchingAumAggregations(true);
    setFetchStatusAumAggregations('fetching');
    try {
      const response = await PrivateWealthTeamsService.pwtAumAggregations(team.id, session);
      const { pwt_aum_aggregations, status } = response.data;
      const withOriginal = pwt_aum_aggregations.map(a => ({
        ...a,
        _original: { ...a }
      }));
      const sorted = sortAggregationsByYearDesc(withOriginal);
      setAumAggregations(sortAggregationsByYearDesc(sorted));
      setFetchStatusAumAggregations(status); // 'fetched'
      onAggregationsChange?.(sorted);
    } catch (error) {
      console.log('fetchAumAggregations#error', JSON.stringify(error));
    } finally {
      setFetchingAumAggregations(false);
    }
  }, [team?.id, session, onAggregationsChange]);

  const handleBlurAumAggregation = useCallback(async(aumAggregation, field) => {
    const current = aumAggregations.find(a => a.id === aumAggregation.id);

    if (!current?._original) {
      return;
    }

    if (current[field] === current._original[field]) {
      return;
    }

    try {
      await PrivateWealthTeamsService.patchAumAggregation(
        team.id,
        session,
        {
          aggregation: {
            id: current.id,
            [field]: current[field],
            automatic: false
          }
        }
      );

      setAumAggregations(prev =>
        prev.map(a =>
          a.id === current.id
            ? { ...a, _original: { ...a, [field]: a[field] } }
            : a
        )
      );
    } catch (error) {
      console.log('handleBlurAumAggregation#error', JSON.stringify(error));
      fetchAumAggregations();
    }
  }, [team?.id, session, aumAggregations, fetchAumAggregations]);

  const handleChangeAumAggregation = useCallback((aumAggregation, field, value) => {
    setAumAggregations(prev => {
      const updated = prev.map(item =>
        item.id === aumAggregation.id
          ? { ...item, [field]: value, automatic: false }
          : item
      );
      onAggregationsChange?.(updated);
      onUserChange?.();
      return updated;
    });
  }, [onAggregationsChange, onUserChange]);

  const handleAddAumAggregation = useCallback(async (formData) => {
    try {
      const aggregationToAdd = {
        year: formData.year,
        month: formData.month,
        total_aum: formData.total_aum,
        minimum_account_size: formData.minimum_account_size,
        source: formData.source,
        automatic: false,
      };
      const response = await PrivateWealthTeamsService.addAumAggregation(
        team.id,
        session,
        { aggregation: aggregationToAdd }
      );
      const { aggregation: aggregationAdded } = response.data;
      const withOriginal = { ...aggregationAdded, _original: { ...aggregationAdded } };
      setAumAggregations(prev => {
        const updated = sortAggregationsByYearDesc([...prev, withOriginal]);
        onAggregationsChange?.(updated);
        onUserChange?.();
        return updated;
      });
    } catch (error) {
      console.log('handleAddAumAggregation#error', JSON.stringify(error));
    }
  }, [team?.id, session, onAggregationsChange, onUserChange]);

  const handleRemoveAumAggregation = useCallback((aumAggregationId) => {
    openConfirmDialog?.({
      title: 'Remove Aum Aggregation',
      message: 'Are you sure you want to remove this aum aggregation?',
      confirmText: 'Remove',
      confirmColor: 'secondary',
      onConfirm: async () => {
        try {
          await PrivateWealthTeamsService.removeAumAggregation(
            team.id,
            session,
            { aggregation_id: aumAggregationId }
          );
          setAumAggregations(prev => {
            const updated = sortAggregationsByYearDesc(prev.filter(a => a.id !== aumAggregationId));
            onAggregationsChange?.(updated);
            onUserChange?.();
            return updated;
          });
        } catch (error) {
          console.log('handleRemoveAumAggregation#error', JSON.stringify(error));
        }
      },
    });
  }, [team?.id, session, openConfirmDialog, onAggregationsChange, onUserChange]);

  return {
    aumAggregations,
    fetchingAumAggregations,
    fetchStatusAumAggregations,

    fetchAumAggregations,
    handleChangeAumAggregation,
    handleAddAumAggregation,
    handleRemoveAumAggregation,
    handleBlurAumAggregation
  };
};

export default useAumAggregations;
